import { appStorage } from '../lib/safeStorage';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight } from 'lucide-react';

const seenKey = (id) => `staffed_spotlight_${id}`;

// Interactive coach-mark tour. On start it filters to the steps whose targets
// are actually on screen (so hidden sections don't create dead/skipped steps),
// then walks exactly those. Highlights each real control with a gold ring +
// short tip. Auto-runs once per tourId; re-trigger with
// window CustomEvent(`staffed:tour:<tourId>`).
export default function SpotlightTour({ tourId, steps = [], autoStart = true, startDelay = 700 }) {
  const [running, setRunning] = useState(false);
  const [active, setActive] = useState([]);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  // We fade/scale the coach-mark in on each step change and animate ONLY
  // opacity/transform — never top/left. The highlight is glued to its target
  // every animation frame instead, so it tracks the smooth-scroll pixel-for-
  // pixel with no position transition fighting the scroll. That fight was the
  // old mobile "shakiness".
  const [shown, setShown] = useState(false);
  const [tipSize, setTipSize] = useState({ w: 360, h: 168 });
  const rafRef = useRef(null);
  const tipRef = useRef(null);

  // Respect the mobile *visual* viewport (area not covered by the dynamic
  // address bar); fall back to the layout viewport.
  const vpW = () => (typeof window !== 'undefined' ? (window.visualViewport?.width ?? window.innerWidth) : 1200);
  const vpH = () => (typeof window !== 'undefined' ? (window.visualViewport?.height ?? window.innerHeight) : 800);

  // Some pages ship duplicate testids for responsive variants (mobile/desktop
  // copies of the same control), one of which is display:none at the current
  // breakpoint. querySelector returns the first match regardless of
  // visibility, which can hand back a hidden zero-rect node and send the
  // spotlight to the top-left corner. Walk all matches and return the first
  // with a real (non-zero) bounding rect.
  const visibleTarget = (sel) => {
    if (!sel) return null;
    let nodes = [];
    try { nodes = document.querySelectorAll(sel); } catch (_e) { return null; }
    for (const n of nodes) {
      const r = n.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return n;
    }
    return null;
  };

  const start = useCallback(() => {
    const present = steps.filter((s) => s && s.target && visibleTarget(s.target));
    if (!present.length) return;
    setActive(present);
    setIdx(0);
    setRunning(true);
  }, [steps]);

  const finish = useCallback(() => {
    setRunning(false);
    setRect(null);
    setShown(false);
    try { appStorage.setItem(seenKey(tourId), '1'); } catch (e) { /* ignore */ }
  }, [tourId]);

  useEffect(() => {
    // Page tours never auto-open. After the one-time welcome walkthrough, every
    // page's tour is available strictly on demand via its "Show me" button —
    // nothing auto-pops as a staffer navigates. Manual button triggers
    // (staffed:tour:${tourId}) still work at any time.
    const onTrigger = () => start();
    window.addEventListener(`staffed:tour:${tourId}`, onTrigger);
    return () => { window.removeEventListener(`staffed:tour:${tourId}`, onTrigger); };
  }, [tourId, autoStart, startDelay, steps.length, start]);

  // Track the active step's target every animation frame and glue the
  // spotlight to it. A rAF loop with change-detection replaces the old
  // 150ms setInterval + scroll/resize listeners: it follows the smooth-scroll
  // pixel-for-pixel (no stutter) and re-renders only when the rect moves.
  useEffect(() => {
    if (!running) return undefined;
    const step = active[idx];
    if (!step) return undefined;
    // Optional per-step hook — lets a step activate its own tab / open a panel
    // before the spotlight tries to locate its target. Errors are swallowed so
    // a broken hook can't wedge the whole tour.
    if (typeof step.beforeShow === 'function') {
      try { step.beforeShow(); } catch (_e) { /* no-op */ }
    }
    setShown(false);
    const el = visibleTarget(step.target);
    if (el) {
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' }); }
      catch (_e) { try { el.scrollIntoView(); } catch (_e2) { /* ignore */ } }
    }
    let prev = null;
    const changed = (r) => !prev
      || Math.abs(prev.top - r.top) > 0.5 || Math.abs(prev.left - r.left) > 0.5
      || Math.abs(prev.width - r.width) > 0.5 || Math.abs(prev.height - r.height) > 0.5;
    const tick = () => {
      const node = visibleTarget(step.target);
      if (node) {
        const r = node.getBoundingClientRect();
        if (changed(r)) {
          prev = { top: r.top, left: r.left, width: r.width, height: r.height };
          setRect(prev);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    const fadeIn = setTimeout(() => setShown(true), 60);
    return () => { cancelAnimationFrame(rafRef.current); clearTimeout(fadeIn); };
  }, [running, idx, active]);

  // Measure the coach-mark so we can keep the WHOLE card on screen.
  useLayoutEffect(() => {
    if (!running || !tipRef.current) return;
    const r = tipRef.current.getBoundingClientRect();
    if (Math.abs(r.height - tipSize.h) > 1 || Math.abs(r.width - tipSize.w) > 1) {
      setTipSize({ w: r.width, h: r.height });
    }
  }, [running, idx, rect, tipSize.h, tipSize.w]);

  useEffect(() => {
    if (!running) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') finish(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, finish]);

  if (!running) return null;
  const step = active[idx];
  if (!step) return null;
  const total = active.length;
  const last = idx >= total - 1;

  const pad = 8;
  const box = rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;

  const vw = vpW();
  const vh = vpH();
  const cardW = Math.min(360, vw - 24);
  const gap = 12;
  const margin = 12;

  // Prefer the side (below/above the highlight) that fits the whole card;
  // otherwise pick the roomier side. Then hard-clamp BOTH axes so the card can
  // never leave the screen — the core mobile "card is lost" fix.
  let tipTop = 120;
  let tipLeft = margin;
  if (box) {
    const spaceBelow = vh - (box.top + box.height);
    const spaceAbove = box.top;
    if (spaceBelow >= tipSize.h + gap + margin) tipTop = box.top + box.height + gap;
    else if (spaceAbove >= tipSize.h + gap + margin) tipTop = box.top - tipSize.h - gap;
    else tipTop = spaceBelow >= spaceAbove ? box.top + box.height + gap : box.top - tipSize.h - gap;
    tipTop = Math.min(Math.max(margin, tipTop), Math.max(margin, vh - tipSize.h - margin));
    tipLeft = Math.min(Math.max(margin, box.left), Math.max(margin, vw - cardW - margin));
  }

  return createPortal(
    <div className="fixed inset-0 z-[9998]" data-testid={`spotlight-tour-${tourId}`}>
      <div className="absolute inset-0" style={{ background: box ? 'transparent' : 'rgba(2,5,15,0.72)' }} />
      {box && (
        <div style={{
          position: 'fixed',
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          borderRadius: 16,
          boxShadow: '0 0 0 9999px rgba(2,5,15,0.72), 0 0 0 2px rgba(212,175,55,0.9), 0 0 30px rgba(212,175,55,0.35)',
          pointerEvents: 'none',
          opacity: shown ? 1 : 0,
          transition: 'opacity 0.22s ease',
          willChange: 'opacity',
        }} />
      )}
      <div
        ref={tipRef}
        style={{
          position: 'fixed',
          top: tipTop,
          left: tipLeft,
          width: cardW,
          maxWidth: 'calc(100vw - 24px)',
          opacity: shown ? 1 : 0,
          transform: shown ? 'scale(1)' : 'scale(0.98)',
          transition: 'opacity 0.25s ease, transform 0.25s cubic-bezier(0.22,1,0.36,1)',
          willChange: 'opacity, transform',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-2xl" style={{ background: 'linear-gradient(180deg,#12162a,#0a0e18)', border: '1px solid rgba(212,175,55,0.35)', boxShadow: '0 20px 50px -12px rgba(0,0,0,0.7)' }}>
          <div className="flex items-start justify-between gap-2 px-4 pt-3.5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E7C75E]">Show me how · {idx + 1}/{total}</p>
            <button onClick={finish} className="text-white/40 hover:text-white/80" data-testid="spotlight-close"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="px-4 pb-4 pt-1">
            <h4 className="text-[16px] font-semibold text-white">{step.title}</h4>
            <p className="text-[13px] text-white/55 mt-1 leading-relaxed">{step.text}</p>
            <div className="flex items-center justify-between mt-3">
              <button onClick={finish} className="text-[11px] text-white/40 hover:text-white/70" data-testid="spotlight-skip">Skip</button>
              <button onClick={() => { if (last) finish(); else setIdx((i) => i + 1); }} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold text-[#0B0F1A]" style={{ background: 'linear-gradient(180deg,#E7C75E,#D4AF37)' }} data-testid="spotlight-next">
                {last ? 'Got it' : 'Next'} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
